import { createSelector } from 'reselect';

import * as app from '../app';
import * as router from '../router';
import * as subscription from '../subscription-v2';
import * as manageAccount from '../manage-account';
import firestoreRedux from '@dreamworld/firestore-redux';
import * as utils from '../../utils';

/**
 * Get quota data for a specific account
 * @param {String} accountId 
 */
const doc = createSelector(
  (state, accountId) => firestoreRedux.selectors.doc(state, `account-invitation-quota`, `aiq_${utils.getIdWoPrefix({ id: accountId || router.selectors.accountId(state), prefix: 'acc_' })}`),
  (doc) => {
    return doc || {};
  }
);

const defaultLimit = createSelector(
  (state, accountId) => app.selectors.config(state),
  (state, accountId) => subscription.selectors.subscription(state, accountId),
  (config, subscription) => {
    if(!subscription || subscription.status === 'TRIALING') {
      return config?.accountInvitationQuota?.limit?.free || 10;
    }
    return config?.accountInvitationQuota?.limit?.paid || -1;
  }
);

/**
 * Get effective daily limit for an account
 * Considers admin override and account type (free/paid)
 * @param {String} accountId 
 */
export const dailyLimit = createSelector(
  (state, accountId) => doc(state, accountId),
  (state, accountId) => defaultLimit(state, accountId),
  (data, defaultLimit) => {
    // If admin has set a specific limit, use that
    if (data && data?.limit !== null && data?.limit !== undefined) {
      return data.limit;
    }
    
    // Otherwise, use default
    return defaultLimit;
  }
);

/**
 * Get remaining invitation limit for today
 * @param {String} accountId 
 */
export const remainingLimit = createSelector(
  (state, accountId) => doc(state, accountId),
  (state, accountId) => dailyLimit(state, accountId),
  (state, accountId) => utils.getCurrentDateByTimezone(manageAccount.selectors.accountOwnerTimeZoneOffset(state, accountId)),
  (quotaData, limit, currentDate) => {
    // If limit is -1, it's unlimited
    if (limit === -1) {
      return -1;
    }

    // If limit is 0, no invitations allowed
    if(limit === 0) {
      return 0;
    }

    // If quota date doesn't match current date, full limit is available
    const currentDateStr = currentDate && currentDate.getTime && utils.getFormatDate(currentDate.getTime(), 'YYYY-MM-DD') || '';
    if (!quotaData || quotaData.date !== currentDateStr) {
      return limit;
    }

    // Calculate remaining
    const used = quotaData.sentCount || 0;
    const remaining = limit - used;
    return Math.max(0, remaining);
  }
);

/**
 * Can the user invite more members today?
 * @param {String} accountId
 * @return {Boolean}
 */
export const canInvite = createSelector(
  (state, accountId) => remainingLimit(state, accountId),
  (remaining) => {
    // Unlimited invitations
    if(remaining === -1) {
      return true;
    }

    // Some invitations left
    return remaining > 0;
  }
);